/**
 * /affordable-dental-implants-fort-worth — dedicated landing page targeting the
 * high-volume "affordable dental implants" query (42k+ impressions in GSC).
 * Intent = "affordable" → we lead with financing / low monthly payments / insurance,
 * NOT dollar amounts. Per practice policy we NEVER publish treatment prices; only the
 * allowed $199/yr in-house plan + $475 free-consult value appear.
 */
import React from 'react';
import { Link } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import AppointmentForm from '@/components/forms/AppointmentForm';
import MonthlyPaymentMatcher from '@/components/forms/MonthlyPaymentMatcher';

const WAYS_TO_SAVE = [
  {
    icon: '💳',
    title: '0% Interest Financing',
    body: 'Break your treatment into low monthly payments through CareCredit and LendingClub — many patients qualify for 0% interest, so implants fit your monthly budget.',
  },
  {
    icon: '🛡️',
    title: 'We Maximize Your Insurance',
    body: 'Most PPO plans cover a portion of implant treatment. We verify your benefits up front and file the claims so you get every dollar you are owed.',
  },
  {
    icon: '🦷',
    title: '$199/yr In-House Dental Plan',
    body: 'No insurance? Our in-house membership plan gives you built-in savings on treatment for a low yearly fee — no deductibles, no waiting periods, no claim forms.',
  },
  {
    icon: '🧾',
    title: 'HSA / FSA Accepted',
    body: 'Use tax-advantaged HSA and FSA dollars toward your implant treatment and stretch your budget even further.',
  },
];

const CITIES = [
  'Fort Worth', 'Arlington', 'Benbrook', 'Lake Worth', 'Aledo',
  'White Settlement', 'Keller', 'Saginaw', 'Haltom City', 'Forest Hill',
  'Crowley', 'Westover Hills',
];

const AffordableDentalImplants = () => {
  const schema = {
    "@context": "https://schema.org",
    "@graph": [
      {
        "@type": "Dentist",
        "@id": "https://fortworthdental.com/#dentist",
        "name": "Fort Worth Dental",
        "url": "https://fortworthdental.com/affordable-dental-implants-fort-worth",
        "telephone": "+18179853337",
        "priceRange": "$$",
        "address": {
          "@type": "PostalAddress",
          "streetAddress": "4620 Citylake Blvd W",
          "addressLocality": "Fort Worth",
          "addressRegion": "TX",
          "postalCode": "76132",
          "addressCountry": "US"
        },
        "aggregateRating": {
          "@type": "AggregateRating",
          "ratingValue": "4.9",
          "reviewCount": "1100"
        }
      },
      {
        "@type": "FAQPage",
        "mainEntity": [
          {
            "@type": "Question",
            "name": "How can I get affordable dental implants in Fort Worth?",
            "acceptedAnswer": {
              "@type": "Answer",
              "text": "Fort Worth Dental makes implants affordable through 0% interest financing (CareCredit, LendingClub), in-house payment plans, a $199/year in-house dental membership, HSA/FSA, and by maximizing your PPO insurance. Instead of a one-size-fits-all price, you get a personalized written quote and a low monthly payment plan at a FREE consultation. Call (817) 985-3337."
            }
          },
          {
            "@type": "Question",
            "name": "Are affordable dental implants lower quality?",
            "acceptedAnswer": {
              "@type": "Answer",
              "text": "No. Affordable means a treatment plan that fits your budget — not a cheaper implant. Your implants are placed by Dr. Mund, a Diplomate of the American Board of Oral Implantology (the top 1% of implant dentists). The most affordable implant is the one done right the first time, so you never pay to fix it later."
            }
          },
          {
            "@type": "Question",
            "name": "Can I finance dental implants with low monthly payments?",
            "acceptedAnswer": {
              "@type": "Answer",
              "text": "Yes. We offer 0% interest financing through CareCredit and LendingClub plus in-house payment plans, so your implant investment becomes a comfortable monthly payment. Approval usually takes just a few minutes in the office."
            }
          },
          {
            "@type": "Question",
            "name": "Does insurance cover dental implants?",
            "acceptedAnswer": {
              "@type": "Answer",
              "text": "Many PPO dental plans cover a portion of implant treatment. Fort Worth Dental accepts most major plans, verifies your benefits in advance, and files your claims so you know your real out-of-pocket before treatment begins."
            }
          },
          {
            "@type": "Question",
            "name": "Do you serve patients outside of Fort Worth?",
            "acceptedAnswer": {
              "@type": "Answer",
              "text": "Yes. Patients travel to Fort Worth Dental from Arlington, Benbrook, Lake Worth, Aledo, White Settlement, Keller and communities across Tarrant County for affordable, expert implant care."
            }
          }
        ]
      }
    ]
  };

  return (
    <div className="min-h-screen bg-white" data-testid="affordable-implants-page">
      <Helmet>
        <title>Affordable Dental Implants in Fort Worth, TX | 0% Financing | Fort Worth Dental</title>
        <meta name="description" content="Affordable dental implants in Fort Worth, TX with 0% interest financing, low monthly payments, insurance maximized, and a $199/yr dental plan. Get your personalized quote at a FREE consultation (a $475 value). Call (817) 985-3337." />
        <meta name="keywords" content="affordable dental implants, affordable dental implants fort worth, affordable dental implants near me, cheap dental implants fort worth, dental implant financing fort worth, low cost dental implants texas, dental implants payment plans, dental implants near me" />
        <link rel="canonical" href="https://fortworthdental.com/affordable-dental-implants-fort-worth" />
        <link rel="alternate" hrefLang="es" href="https://fortworthdental.com/es/implantes-dentales-economicos-fort-worth" />
        <link rel="alternate" hrefLang="en" href="https://fortworthdental.com/affordable-dental-implants-fort-worth" />
        <link rel="alternate" hrefLang="x-default" href="https://fortworthdental.com/affordable-dental-implants-fort-worth" />
        <script type="application/ld+json">{JSON.stringify(schema)}</script>
      </Helmet>

      {/* Hero */}
      <section className="bg-gradient-to-br from-teal-600 to-teal-800 text-white py-20">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl">
            <p className="text-teal-200 font-semibold mb-2">Fort Worth, TX • Financing Available</p>
            <h1 className="text-4xl sm:text-5xl font-bold mb-6" data-testid="affordable-implants-h1">
              Affordable Dental Implants in Fort Worth
            </h1>
            <p className="text-xl mb-6">
              A new smile shouldn't break the bank. With 0% interest financing, low monthly payments,
              and insurance maximized, quality dental implants are within reach — placed by a
              board-certified implant specialist.
            </p>
            <div className="bg-white/10 p-6 rounded-lg mb-8">
              <div className="text-2xl font-bold text-yellow-300">FREE Implant Consultation</div>
              <p className="text-teal-100">3D scan, X-rays, and a written treatment plan with your payment options — a $475 value</p>
            </div>
            <div className="flex flex-col sm:flex-row gap-4">
              <a href="tel:8179853337" data-testid="affordable-implants-hero-call" className="bg-white text-teal-700 px-8 py-4 rounded-lg font-bold text-center hover:bg-gray-100">
                Call (817) 985-3337
              </a>
              <Link to="/dental-implant-cost-fort-worth" className="border-2 border-white text-white px-8 py-4 rounded-lg font-bold text-center hover:bg-white/10">
                See What Affects Your Quote
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* Intro */}
      <section className="py-16 bg-white">
        <div className="container mx-auto px-4 max-w-5xl">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-10 items-start">
            <div>
              <h2 className="text-3xl font-bold text-gray-800 mb-6">
                Quality Implants That Fit Your Budget
              </h2>
              <p className="text-lg text-gray-700 mb-6 leading-relaxed">
                "Affordable" doesn't mean a cut-rate implant — it means a treatment plan that works for
                your life. Instead of publishing a generic price that won't match your case, Fort Worth
                Dental gives you a clear, itemized written quote and a payment plan you can actually
                afford. Most patients are surprised how manageable the monthly payment becomes once
                financing and insurance are applied.
              </p>
              <p className="text-lg text-gray-700 leading-relaxed">
                And because your implants are placed by Dr. Mund — a Diplomate of the American Board of
                Oral Implantology — you get it done right the first time. That's the most affordable
                implant of all: the one you never have to pay to fix.
              </p>
            </div>
            <div className="lg:sticky lg:top-24">
              <MonthlyPaymentMatcher />
            </div>
          </div>
        </div>
      </section>

      {/* Ways to Save */}
      <section className="py-16 bg-gray-50">
        <div className="container mx-auto px-4">
          <h2 className="text-3xl font-bold text-gray-800 mb-4 text-center">
            4 Ways We Make Implants Affordable
          </h2>
          <p className="text-center text-gray-600 mb-10 max-w-2xl mx-auto">
            Your treatment coordinator walks you through every option at your free consultation so
            you leave knowing exactly what you'll pay per month.
          </p>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 max-w-4xl mx-auto">
            {WAYS_TO_SAVE.map((f) => (
              <div key={f.title} className="bg-white p-6 rounded-lg shadow flex gap-4" data-testid={`save-card-${f.title}`}>
                <div className="text-3xl flex-shrink-0" aria-hidden="true">{f.icon}</div>
                <div>
                  <h3 className="font-bold text-gray-900 mb-2">{f.title}</h3>
                  <p className="text-gray-600 text-sm">{f.body}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Trust / Reviews */}
      <section className="py-16">
        <div className="container mx-auto px-4 max-w-5xl">
          <h2 className="text-3xl font-bold text-gray-800 mb-8 text-center">Why Fort Worth Trusts Us With Their Smile</h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            <div className="bg-teal-50 p-6 rounded-lg text-center">
              <div className="text-4xl font-extrabold text-teal-700 mb-1">1,100+</div>
              <p className="text-gray-700 font-semibold">5-Star Google Reviews</p>
            </div>
            <div className="bg-teal-50 p-6 rounded-lg text-center">
              <div className="text-4xl font-extrabold text-teal-700 mb-1">10,000+</div>
              <p className="text-gray-700 font-semibold">Implants Placed by Dr. Mund</p>
            </div>
            <div className="bg-teal-50 p-6 rounded-lg text-center">
              <div className="text-4xl font-extrabold text-teal-700 mb-1">Top 1%</div>
              <p className="text-gray-700 font-semibold">ABOI Board-Certified Specialist</p>
            </div>
          </div>
        </div>
      </section>

      {/* Cities served */}
      <section className="py-16 bg-gray-50">
        <div className="container mx-auto px-4 max-w-4xl">
          <h2 className="text-3xl font-bold text-gray-800 mb-4 text-center">
            Affordable Dental Implants Near You
          </h2>
          <p className="text-center text-gray-600 mb-8">
            Patients drive to Fort Worth Dental from across Tarrant County for affordable, expert implant care:
          </p>
          <div className="flex flex-wrap justify-center gap-3">
            {CITIES.map((city) => (
              <span key={city} className="bg-white border border-teal-200 text-teal-800 px-4 py-2 rounded-full text-sm font-medium">
                {city}
              </span>
            ))}
          </div>
        </div>
      </section>

      {/* FAQ */}
      <section className="py-16">
        <div className="container mx-auto px-4 max-w-4xl">
          <h2 className="text-3xl font-bold text-gray-800 mb-8 text-center">Affordable Dental Implants — FAQ</h2>
          <div className="space-y-6">
            <div className="bg-gray-50 p-6 rounded-lg">
              <h3 className="font-bold text-gray-800 mb-2">How can I get affordable dental implants in Fort Worth?</h3>
              <p className="text-gray-600">We make implants affordable through 0% interest financing (CareCredit, LendingClub), in-house payment plans, a $199/year in-house dental plan, HSA/FSA, and by maximizing your PPO insurance. You get a personalized written quote and a low monthly payment at a FREE consultation. Call (817) 985-3337.</p>
            </div>
            <div className="bg-gray-50 p-6 rounded-lg">
              <h3 className="font-bold text-gray-800 mb-2">Are affordable dental implants lower quality?</h3>
              <p className="text-gray-600">No. Affordable means a treatment plan that fits your budget — not a cheaper implant. Your implants are placed by Dr. Mund, a Diplomate of the American Board of Oral Implantology. The most affordable implant is the one done right the first time.</p>
            </div>
            <div className="bg-gray-50 p-6 rounded-lg">
              <h3 className="font-bold text-gray-800 mb-2">Can I finance dental implants with low monthly payments?</h3>
              <p className="text-gray-600">Yes. We offer 0% interest financing through CareCredit and LendingClub plus in-house payment plans, so your implant investment becomes a comfortable monthly payment. Approval usually takes just a few minutes in the office.</p>
            </div>
            <div className="bg-gray-50 p-6 rounded-lg">
              <h3 className="font-bold text-gray-800 mb-2">Does insurance cover dental implants?</h3>
              <p className="text-gray-600">Many PPO dental plans cover a portion of implant treatment. We accept most major plans, verify your benefits in advance, and file your claims so you know your real out-of-pocket before treatment begins.</p>
            </div>
            <div className="bg-gray-50 p-6 rounded-lg">
              <h3 className="font-bold text-gray-800 mb-2">Do you serve patients outside of Fort Worth?</h3>
              <p className="text-gray-600">Yes. Patients travel to us from Arlington, Benbrook, Lake Worth, Aledo, White Settlement, Keller and communities across Tarrant County for affordable, expert implant care.</p>
            </div>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-16 bg-teal-600 text-white">
        <div className="container mx-auto px-4 text-center">
          <h2 className="text-3xl font-bold mb-4">See How Affordable Your New Smile Can Be</h2>
          <p className="text-xl mb-8">Get your personalized quote and monthly payment options at a FREE consultation — a $475 value.</p>
          <a href="tel:8179853337" data-testid="affordable-implants-cta-call" className="bg-white text-teal-600 px-8 py-4 rounded-lg font-bold text-lg hover:bg-gray-100 inline-block">
            Call (817) 985-3337
          </a>
        </div>
      </section>

      {/* Form */}
      <section className="py-16">
        <div className="container mx-auto px-4 max-w-2xl">
          <h2 className="text-3xl font-bold text-gray-800 mb-8 text-center">Request Your FREE Implant Consultation</h2>
          <AppointmentForm source="affordable_implants_page" />
        </div>
      </section>
    </div>
  );
};

export default AffordableDentalImplants;
